import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { FileText, Download, Loader2, PiggyBank, Percent, Bell, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtTok = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}k` : String(n || 0));

const TenantStatementCard = ({ t, onPdf, pdfBusy, onEmail, emailBusy }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03]" data-testid={`statement-tenant-${t.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}>
    <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-3 flex-wrap">
      <div>
        <p className="font-semibold text-white">{t.name}</p>
        <p className="text-[11px] text-white/40">
          {t.calls} AI calls · {fmtTok(t.tokens_in)} in / {fmtTok(t.tokens_out)} out · {t.share_pct}% of total
        </p>
      </div>
      <div className="text-right">
        <p className="text-xl font-bold text-teal-400 tabular-nums">${t.total_usd.toFixed(2)}</p>
        {t.charge_usd > t.total_usd && (
          <p className="text-sm font-semibold text-emerald-400 tabular-nums" data-testid={`statement-charge-${t.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}>
            charge: ${t.charge_usd.toFixed(2)}
          </p>
        )}
        <p className="text-[11px] text-white/40">prev month: ${t.prev_month_usd.toFixed(2)}</p>
      </div>
    </div>
    <div className="px-4 pb-3">
      <table className="w-full text-sm">
        <tbody>
          {t.categories.map((c) => (
            <tr key={c.category} className="border-t border-white/10">
              <td className="py-1.5 text-white/70">{c.category}</td>
              <td className="py-1.5 text-right text-white/40 text-xs">{c.calls} calls</td>
              <td className="py-1.5 text-right text-white/90 tabular-nums w-24">${c.cost_usd.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {t.batch_saved_usd > 0 && (
        <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1.5" data-testid={`statement-batch-saved-${t.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}>
          <PiggyBank className="w-3.5 h-3.5" />
          Batch API already saved ${t.batch_saved_usd.toFixed(2)} on this tenant (discount applied above)
        </p>
      )}
      {!t.is_shared && (
        <div className="mt-2 flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => onEmail(t.name)} disabled={emailBusy === t.name}
            className="border-white/20 text-white/80 hover:bg-white/10 bg-transparent"
            data-testid={`statement-email-${t.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}>
            {emailBusy === t.name ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
            Email invoice
          </Button>
          <Button size="sm" variant="outline" onClick={() => onPdf(t.name)} disabled={pdfBusy === t.name}
            className="border-white/20 text-white/80 hover:bg-white/10 bg-transparent"
            data-testid={`statement-pdf-${t.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}>
            {pdfBusy === t.name ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
            PDF invoice
          </Button>
        </div>
      )}
    </div>
  </div>
);

export default function AdminCostStatementsPage() {
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(thisMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [markupDraft, setMarkupDraft] = useState('');
  const [savingMarkup, setSavingMarkup] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(null);
  const [emailBusy, setEmailBusy] = useState(null);
  const [contacts, setContacts] = useState({});

  useEffect(() => {
    axios.get(`${API}/admin/llm-costs/billing-contacts`).then((r) => setContacts(r.data.contacts || {})).catch(() => {});
  }, []);

  const emailInvoice = async (tenantName) => {
    const existing = contacts[tenantName] || '';
    const to = window.prompt(`Email ${tenantName}'s ${month} invoice to (billing contact):`, existing);
    if (to === null) return;
    setEmailBusy(tenantName);
    try {
      const r = await axios.post(`${API}/admin/llm-costs/statement/email`, { tenant: tenantName, month, to: to.trim() });
      setContacts((p) => ({ ...p, [tenantName]: r.data.sent_to }));
      toast.success(`Invoice emailed to ${r.data.sent_to} ($${r.data.charge_usd.toFixed(2)})`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Email failed'); }
    setEmailBusy(null);
  };
  const [alertInfo, setAlertInfo] = useState(null);
  const [alertDraft, setAlertDraft] = useState('');
  const [savingAlert, setSavingAlert] = useState(false);
  const [cutover, setCutover] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/llm-costs/cutover-savings`).then((r) => setCutover(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/admin/llm-costs/spend-alert`)
      .then((r) => { setAlertInfo(r.data); setAlertDraft(String(r.data.threshold_usd ?? 0)); })
      .catch(() => {});
  }, []);

  const saveAlert = async () => {
    setSavingAlert(true);
    try {
      const r = await axios.patch(`${API}/admin/llm-costs/spend-alert`, { threshold_usd: Number(alertDraft) });
      toast.success(r.data.threshold_usd > 0
        ? `Spend alert set: email when a day's Claude spend passes $${r.data.threshold_usd}`
        : 'Spend alerts turned off');
      setAlertInfo((p) => ({ ...p, threshold_usd: r.data.threshold_usd }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save alert'); }
    setSavingAlert(false);
  };

  const downloadPdf = async (tenantName) => {
    setPdfBusy(tenantName);
    try {
      const r = await axios.get(`${API}/admin/llm-costs/statement.pdf`, {
        params: { month, tenant: tenantName }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-statement-${tenantName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-${month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('PDF download failed'); }
    setPdfBusy(null);
  };

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/llm-costs/statement`, { params: { month } })
      .then((r) => { setData(r.data); setMarkupDraft(String(r.data.markup_pct ?? 0)); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load statement'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [month]);

  const saveMarkup = async () => {
    setSavingMarkup(true);
    try {
      const r = await axios.patch(`${API}/admin/llm-costs/markup`, { markup_pct: Number(markupDraft) });
      toast.success(`Markup set to ${r.data.markup_pct}%`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save markup'); }
    setSavingMarkup(false);
  };

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/admin/llm-costs/statement.csv`, { params: { month }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-cost-statement-${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('CSV download failed'); }
    setDownloading(false);
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-4" data-testid="cost-statements-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-teal-400" /> AI Cost Statements
          </h1>
          <div className="flex items-center gap-2">
            <input
              type="month"
              value={month}
              max={thisMonth}
              onChange={(e) => setMonth(e.target.value)}
              className="border border-white/15 rounded-md text-sm px-2 py-1.5 bg-[#0A0F1C] text-white/90"
              data-testid="statement-month-picker"
            />
            <Button onClick={downloadCsv} disabled={downloading || !data} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="statement-download-csv">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
              Download CSV
            </Button>
          </div>
        </div>

        {cutover && (
          <div className="rounded-xl border border-teal-500/30 bg-teal-500/[0.06] px-4 py-3" data-testid="cutover-savings-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-sm font-semibold text-teal-300">Prompt-caching savings since v2 cutover ({cutover.cutover_date})</p>
                <p className="text-[11px] text-white/40">What grading actually cost vs what the same calls would have cost without caching. Watch this build over the week.</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-teal-300 tabular-nums" data-testid="cutover-total-saved">
                  ${(cutover.total_saved_usd || 0).toFixed(2)} saved
                </p>
                <p className="text-[11px] text-white/50">
                  ${(cutover.total_actual_usd || 0).toFixed(2)} actual vs ${(cutover.total_baseline_usd || 0).toFixed(2)} uncached
                  {cutover.saved_pct > 0 && ` · ${cutover.saved_pct}% off`}
                </p>
              </div>
            </div>
            {(cutover.days || []).length > 0 ? (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6" data-testid="cutover-daily-rows">
                {cutover.days.slice(0, 8).map((d) => (
                  <div key={d.date} className="flex items-center justify-between text-xs py-0.5 border-b border-white/5">
                    <span className="text-white/50">{d.date} · {d.calls} calls</span>
                    <span className="tabular-nums text-white/70">${d.actual_usd.toFixed(2)}
                      <span className={d.saved_usd > 0 ? 'text-teal-400 ml-2' : 'text-white/30 ml-2'}>−${d.saved_usd.toFixed(2)}</span>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-white/40 mt-1" data-testid="cutover-no-data">No graded calls since cutover yet — this fills in as calls come through this week.</p>
            )}
          </div>
        )}

        {loading && <p className="text-white/40 text-sm">Loading…</p>}
        {!loading && data && (
          <>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-sm font-semibold text-white">{data.label}{data.partial_month ? ' (month to date)' : ''}</p>
                <p className="text-[11px] text-white/40">Per-customer AI cost statement — use for pricing or rebilling.</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-white tabular-nums" data-testid="statement-grand-total">${data.total_usd.toFixed(2)}</p>
                {data.markup_pct > 0 && (
                  <p className="text-sm font-semibold text-emerald-400 tabular-nums" data-testid="statement-grand-charge">
                    to charge: ${data.total_charge_usd.toFixed(2)}
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="statement-markup-row">
              <Percent className="w-4 h-4 text-teal-400 shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-semibold text-white">Rebilling markup</p>
                <p className="text-[11px] text-white/40">Applied on top of cost to produce the "charge" price on every statement and CSV.</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number" min="0" max="1000" step="5"
                  value={markupDraft}
                  onChange={(e) => setMarkupDraft(e.target.value)}
                  className="w-24 border border-white/15 rounded-md text-sm px-2 py-1.5 bg-[#0A0F1C] text-white/90 text-right"
                  data-testid="statement-markup-input"
                />
                <span className="text-white/50 text-sm">%</span>
                <Button size="sm" onClick={saveMarkup}
                  disabled={savingMarkup || Number(markupDraft) === (data.markup_pct ?? 0)}
                  className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="statement-markup-save">
                  {savingMarkup ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
            {data.tenants.length === 0 && <p className="text-white/40 text-sm italic">No AI usage recorded for this month.</p>}
            {data.tenants.map((t) => <TenantStatementCard key={t.name} t={t} onPdf={downloadPdf} pdfBusy={pdfBusy} onEmail={emailInvoice} emailBusy={emailBusy} />)}

            <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="spend-alert-row">
              <Bell className="w-4 h-4 text-amber-400 shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-semibold text-white">Daily Claude spend alert</p>
                <p className="text-[11px] text-white/40">
                  Emails all master admins the moment a single day's Claude spend crosses this amount (max one email/day). 0 = off.
                  {alertInfo?.today && <> Today so far: <span className="text-white/70 font-semibold">${alertInfo.today.spend_usd.toFixed(2)}</span></>}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white/50 text-sm">$</span>
                <input
                  type="number" min="0" step="5"
                  value={alertDraft}
                  onChange={(e) => setAlertDraft(e.target.value)}
                  className="w-24 border border-white/15 rounded-md text-sm px-2 py-1.5 bg-[#0A0F1C] text-white/90 text-right"
                  data-testid="spend-alert-input"
                />
                <Button size="sm" onClick={saveAlert}
                  disabled={savingAlert || Number(alertDraft) === (alertInfo?.threshold_usd ?? 0)}
                  className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="spend-alert-save">
                  {savingAlert ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
            <p className="text-[11px] text-white/30">
              Costs are estimates from list prices per 1M tokens. Batch API discounts are already reflected.
              "Platform / shared" covers admin tools and platform jobs that aren't attributable to a single tenant.
            </p>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
